import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import { Icon } from "@iconify/react/dist/iconify.js";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = process.env.REACT_APP_API;

const SignInLayer = () => {
  const [username, setusername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginloading, setLoginLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginLoading(true);
    try {
      const response = await fetch(`${baseUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }
      NotificationManager.success("Logged in successful!");
      sessionStorage.setItem("AdminAuthToken", data.token);
      navigate("/media");
    } catch (error) {
      NotificationManager.error(error.message || "Login failed. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("AdminAuthToken");
    if (token) {
      navigate("/media");
    } else {
      setLoading(false);
    }
  }, []);

  const inputBase = {
    width: "100%",
    padding: "0.95rem 1rem 0.95rem 2.85rem",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s ease",
    outline: "none",
    fontSize: "0.95rem",
    color: "#1a202c",
  };

  const inputFocused = {
    borderColor: "#4f46e5",
    backgroundColor: "#ffffff",
    boxShadow: "0 0 0 4px rgba(79, 70, 229, 0.12)",
  };

  return (
    <>
      {loading ? (
        <div className="loading-screen">
          <div className="logo-container">
            <div className="spinner"></div>
          </div>
        </div>
      ) : (
        <section
          style={{
            minHeight: "100vh",
            display: "flex",
            background:
              "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 50%, #fdf2f8 100%)",
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "1fr",
              alignItems: "center",
              justifyItems: "center",
              padding: "1.5rem",
            }}
            className="signin-grid"
          >
            <div
              style={{
                width: "100%",
                maxWidth: "1000px",
                background: "#ffffff",
                borderRadius: "20px",
                boxShadow:
                  "0 20px 50px rgba(31, 38, 135, 0.12), 0 8px 16px rgba(0, 0, 0, 0.04)",
                overflow: "hidden",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
              }}
              className="signin-card"
            >
              {/* Left branding panel */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)",
                  padding: "3rem 2.5rem",
                  color: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: "560px",
                }}
                className="signin-brand"
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-80px",
                    right: "-80px",
                    width: "260px",
                    height: "260px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.08)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "-60px",
                    left: "-60px",
                    width: "200px",
                    height: "200px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.06)",
                  }}
                />

                <div style={{ position: "relative", zIndex: 1 }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.6rem",
                      padding: "0.5rem 0.9rem",
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.18)",
                      backdropFilter: "blur(8px)",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                    }}
                  >
                    <Icon icon="mdi:shield-check" width="16" />
                    Admin Panel
                  </div>
                </div>

                <div style={{ position: "relative", zIndex: 1 }}>
                  <h1
                    style={{
                      fontSize: "2.25rem",
                      fontWeight: 700,
                      lineHeight: 1.15,
                      marginBottom: "1rem",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Welcome back to <br /> Sidharth's Portfolio
                  </h1>
                  <p
                    style={{
                      fontSize: "0.95rem",
                      lineHeight: 1.6,
                      opacity: 0.92,
                      marginBottom: "2rem",
                      maxWidth: "340px",
                    }}
                  >
                    Sign in to manage content, monitor activity, and keep
                    everything running smoothly.
                  </p>

                  <div style={{ display: "grid", gap: "0.85rem" }}>
                    {[
                      { icon: "mdi:flash", text: "Fast & secure access" },
                      { icon: "mdi:chart-line", text: "Real-time insights" },
                      { icon: "mdi:account-cog", text: "Full control center" },
                    ].map((f) => (
                      <div
                        key={f.text}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.7rem",
                          fontSize: "0.9rem",
                          opacity: 0.95,
                        }}
                      >
                        <span
                          style={{
                            width: "30px",
                            height: "30px",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.2)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon icon={f.icon} width="16" />
                        </span>
                        {f.text}
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    fontSize: "0.8rem",
                    opacity: 0.8,
                  }}
                >
                  © {new Date().getFullYear()} Sidharth Sogani. All rights
                  reserved.
                </div>
              </div>

              {/* Right form panel */}
              <div
                style={{
                  padding: "3rem 2.75rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                className="signin-form-panel"
              >
                <div style={{ marginBottom: "2rem" }}>
                  <h2
                    style={{
                      fontSize: "1.75rem",
                      fontWeight: 700,
                      color: "#1a202c",
                      marginBottom: "0.4rem",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Sign in
                  </h2>
                  <p
                    style={{
                      color: "#64748b",
                      fontSize: "0.95rem",
                      margin: 0,
                    }}
                  >
                    Enter your credentials to access your dashboard.
                  </p>
                </div>

                <form onSubmit={handleLogin} autoComplete="on">
                  <div style={{ marginBottom: "1.25rem" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "#334155",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Username
                    </label>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "1rem",
                          transform: "translateY(-50%)",
                          color:
                            focusedField === "username" ? "#4f46e5" : "#94a3b8",
                          transition: "color 0.2s",
                          display: "inline-flex",
                        }}
                      >
                        <Icon icon="mdi:account-outline" width="20" />
                      </span>
                      <input
                        type="text"
                        style={{
                          ...inputBase,
                          ...(focusedField === "username" ? inputFocused : {}),
                        }}
                        placeholder="Enter your username"
                        value={username}
                        onChange={(e) => setusername(e.target.value)}
                        onFocus={() => setFocusedField("username")}
                        onBlur={() => setFocusedField(null)}
                        autoComplete="username"
                        required
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: "0.75rem" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "#334155",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Password
                    </label>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "1rem",
                          transform: "translateY(-50%)",
                          color:
                            focusedField === "password" ? "#4f46e5" : "#94a3b8",
                          transition: "color 0.2s",
                          display: "inline-flex",
                        }}
                      >
                        <Icon icon="solar:lock-password-outline" width="20" />
                      </span>
                      <input
                        type={showPassword ? "text" : "password"}
                        style={{
                          ...inputBase,
                          paddingRight: "2.85rem",
                          ...(focusedField === "password" ? inputFocused : {}),
                        }}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setFocusedField("password")}
                        onBlur={() => setFocusedField(null)}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "0.75rem",
                          transform: "translateY(-50%)",
                          background: "transparent",
                          border: "none",
                          color: "#94a3b8",
                          cursor: "pointer",
                          padding: "0.25rem",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          icon={showPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"}
                          width="20"
                        />
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1.5rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.45rem",
                        color: "#475569",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{
                          width: "16px",
                          height: "16px",
                          accentColor: "#4f46e5",
                          cursor: "pointer",
                        }}
                      />
                      Remember me
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loginloading}
                    style={{
                      width: "100%",
                      padding: "0.95rem 1rem",
                      borderRadius: "10px",
                      background: loginloading
                        ? "#818cf8"
                        : "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                      color: "white",
                      fontWeight: 600,
                      border: "none",
                      cursor: loginloading ? "not-allowed" : "pointer",
                      transition: "transform 0.15s ease, box-shadow 0.2s ease",
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.6rem",
                      boxShadow: "0 8px 20px rgba(79, 70, 229, 0.25)",
                    }}
                    onMouseDown={(e) => {
                      if (!loginloading)
                        e.currentTarget.style.transform = "translateY(1px)";
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {loginloading ? (
                      <>
                        <span
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid rgba(255,255,255,0.4)",
                            borderTopColor: "#ffffff",
                            borderRadius: "50%",
                            display: "inline-block",
                            animation: "signin-spin 0.7s linear infinite",
                          }}
                        />
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign In
                        <Icon icon="mdi:arrow-right" width="18" />
                      </>
                    )}
                  </button>
                </form>

                <p
                  style={{
                    marginTop: "1.75rem",
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: "0.8rem",
                  }}
                >
                  Protected by industry-standard encryption
                </p>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes signin-spin {
              to { transform: rotate(360deg); }
            }
            @media (max-width: 900px) {
              .signin-card {
                grid-template-columns: 1fr !important;
                max-width: 480px !important;
              }
              .signin-brand {
                min-height: auto !important;
                padding: 2rem 1.75rem !important;
              }
              .signin-brand h1 {
                font-size: 1.6rem !important;
              }
              .signin-form-panel {
                padding: 2rem 1.75rem !important;
              }
            }
            @media (max-width: 480px) {
              .signin-brand {
                display: none !important;
              }
            }
          `}</style>
        </section>
      )}
    </>
  );
};

export default SignInLayer;
